import type { Location } from 'react-router';

import LoggingService from 'components/core/logging/LoggingService';
import { NO_AUTH_REQUIRED } from 'enums/apollo';
import { RoutePath } from 'enums/routePath';

import { getDefaultHeaders } from 'store/api/ApiRequest';
import { logApiError } from 'store/api/graph/interfaces/apiErrors';
import { SsoLoginMutation, UserLoginMutation, UserLogoutMutation } from 'store/api/graph/mutations/auth';
import { userLoginQuery } from 'store/api/graph/queries/auth';

import { importWithStaleChunkRecovery } from './chunkRecoveryUtils';
import { getDateTime } from './dateUtils';
import { isIE, isMobileDevice } from './deviceInfoUtils';
import { authStorage, lastLoginStorage, userIdStorage } from './storage/auth';

export const isLoggedIn = () => (isIE() || isMobileDevice() ? false : !!authStorage.get());

const cacheAuth = (token: string, time: number) => {
  authStorage.set(`Bearer ${token}`);
  lastLoginStorage.set(time);
};

const clearCacheAuth = () => {
  authStorage.remove();
  lastLoginStorage.remove();
};

class AuthManager {
  lastLogin?: number;
  routingTarget?: Location;

  getClient = () =>
    importWithStaleChunkRecovery(() => import('store/apollo/ApolloClient')).then(({ client, subscriptionClient }) => ({
      client,
      subscriptionClient,
    }));

  getUser = (): Promise<any> =>
    this.getClient().then(({ client }) =>
      client
        .query({ fetchPolicy: 'no-cache', query: userLoginQuery })
        .then(response => {
          userIdStorage.set(response.data!.user.id);

          return { ...response, lastLogin: this.lastLogin };
        })
        .catch((error: Error) => {
          logApiError(error);
          clearCacheAuth();

          return error;
        })
    );

  logout = async () => {
    const authenticatedHeaders = getDefaultHeaders();

    this.routingTarget = undefined;

    clearCacheAuth();
    this.closeSubscription();
    await this.getClient()
      .then(({ client }) => client.clearStore())
      .catch((error: Error) =>
        LoggingService.warn({
          message: 'AuthUtils.ts: Failed to clear Apollo store during logout',
          messageContext: error,
        })
      );
    await this.tryUserLogout(authenticatedHeaders);
    await this.endImpersonation();
    globalThis.location.replace(RoutePath.LOGIN);
  };

  tryLogin = (): Promise<any> => {
    const token = authStorage.get();

    return new Promise((resolve, reject) => {
      if (token) {
        this.getUser()
          .then(resolve)
          .catch((error: Error) => reject(error));
      } else {
        resolve(undefined);
      }
    });
  };

  trySsoLogin = (authorizationCode: string): Promise<any> => {
    const ssoLoginOptions = {
      context: { headers: NO_AUTH_REQUIRED },
      mutation: SsoLoginMutation,
      variables: { authorizationCode },
    };

    return this.getClient().then(({ client }) =>
      client.mutate(ssoLoginOptions).then(response => {
        this.tryCacheAuth(response?.data?.ssoLogin);

        return this.getUser();
      })
    );
  };

  tryUserLogin = (username: string, password: string): Promise<any> => {
    const loginOptions = {
      context: { headers: NO_AUTH_REQUIRED },
      mutation: UserLoginMutation,
      variables: { password, username },
    };

    return this.getClient().then(({ client }) =>
      client.mutate(loginOptions).then(response => {
        this.tryCacheAuth(response?.data?.userLogin);

        return this.getUser();
      })
    );
  };

  /**
   * Attempts to have the API dispose of the user's active session (or all, if supplied).
   */
  tryUserLogout = (headers = {}, allSessions = false) =>
    this.getClient().then(({ client }) =>
      client
        .mutate({
          context: { headers },
          mutation: UserLogoutMutation,
          variables: { allSessions },
        })
        .catch((error: Error) =>
          LoggingService.warn({
            message: 'AuthUtils.ts: Auto logout, likely an invalid token',
            messageContext: error,
          })
        )
    );

  private closeSubscription = () =>
    void this.getClient().then(({ subscriptionClient }) => {
      subscriptionClient?.close();
    });

  private endImpersonation = () =>
    importWithStaleChunkRecovery(() => import('utils/impersonationUtils')).then(({ impersonationManager }) =>
      impersonationManager.endImpersonation()
    );

  private tryCacheAuth(token?: null | string) {
    if (!token) {
      return;
    }

    this.lastLogin = getDateTime().toMillis();
    cacheAuth(token, this.lastLogin);

    this.closeSubscription();
  }
}

export const authManager = new AuthManager();
